import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiGet, apiPost, ApiError } from '../lib/api.ts'
import { useSelectedCampaign } from '../lib/campaign.tsx'

/**
 * Cutting a list, and taking delivery of it.
 *
 * Nothing about a list type is hardcoded here — the shape, the columns and
 * whether it prints all arrive from /api/list-types, which is why that endpoint
 * exists (§6b: custom list types are data). A campaign that defines its own
 * fourth type gets it in this dropdown without a deploy.
 */

interface ListType {
  list_type_id: string
  code: string
  name: string
  description: string | null
  shape: string
  page_break: string[]
  response_codes: string[]
  household_context: boolean
  is_builtin: boolean
}

interface SavedUniverse {
  universe_id: string
  name: string
  version_no: number | null
  row_count: string | number | null
}

interface ListExport {
  export_id: string
  name: string | null
  status: string
  row_count: string | null
  list_type_code: string
  list_type_name: string
  universe_name: string
  version_no: number
  requested_at: string
  completed_at: string | null
  rows_expire_at: string | null
  error: string | null
}

/**
 * Whether a list type is meant for paper.
 *
 * Derived rather than a fourth column: a type carries response codes or page
 * breaks exactly when someone designed it to be printed. A mail list has
 * neither — it is a file for a mail house, and offering to print four hundred
 * pages of it would be an invitation to a mistake. Only an affordance; the
 * route itself will render any completed export.
 */
function isPrintable(type: ListType | undefined): boolean {
  if (!type) return false
  return type.response_codes.length > 0 || type.page_break.length > 0
}

const ACTIVE = new Set(['queued', 'running'])

export function Lists() {
  const { campaignId, campaign } = useSelectedCampaign()
  const client = useQueryClient()

  const [universeId, setUniverseId] = useState('')
  const [listTypeId, setListTypeId] = useState('')
  const [name, setName] = useState('')

  const universes = useQuery({
    queryKey: ['universes', campaignId],
    queryFn: () => apiGet<{ universes: SavedUniverse[] }>(`/universes?campaign_id=${campaignId}`),
    enabled: Boolean(campaignId),
  })

  const listTypes = useQuery({
    queryKey: ['list-types', campaignId],
    queryFn: () => apiGet<{ list_types: ListType[] }>(`/list-types?campaign_id=${campaignId}`),
    enabled: Boolean(campaignId),
  })

  const exports = useQuery({
    queryKey: ['exports', campaignId],
    queryFn: () => apiGet<{ exports: ListExport[] }>(`/exports?campaign_id=${campaignId}`, { background: true }),
    enabled: Boolean(campaignId),
    // A cut is a queued job, so the table has to find out when it finishes.
    // Polls only while something is actually in flight rather than on a timer
    // that runs all day against a screen nobody is watching.
    refetchInterval: (query) =>
      query.state.data?.exports.some((e) => ACTIVE.has(e.status)) ? 2000 : false,
  })

  // Default the two pickers once their options arrive, so the common case is
  // one click rather than three.
  useEffect(() => {
    const first = universes.data?.universes[0]
    if (first && !universeId) setUniverseId(first.universe_id)
  }, [universes.data, universeId])

  /*
   * A Field lieutenant cuts walk lists only (2026-09-30); everyone else who may
   * cut sees every type. The server refuses the rest regardless — this keeps
   * the picker from offering a type the request would then fail on.
   */
  const cuttableTypes = (listTypes.data?.list_types ?? []).filter(
    (t) => campaign?.can_build_universes === true || t.code === 'walk',
  )

  useEffect(() => {
    const first = cuttableTypes[0]
    if (first && !cuttableTypes.some((t) => t.list_type_id === listTypeId)) {
      setListTypeId(first.list_type_id)
    }
  }, [cuttableTypes, listTypeId])

  const cut = useMutation({
    mutationFn: async () => {
      const universe = universes.data?.universes.find((u) => u.universe_id === universeId)
      if (!universe) throw new Error('Pick a universe first.')
      return apiPost<{ export_id: string }>('/exports', {
        campaign_id: campaignId,
        // The version, never the universe: editing it next week must not
        // rewrite what this export claims to have been.
        universe_version_id: await versionIdFor(universe.universe_id),
        list_type_id: listTypeId,
        ...(name.trim() ? { name: name.trim() } : {}),
      })
    },
    onSuccess: async () => {
      setName('')
      await client.invalidateQueries({ queryKey: ['exports', campaignId] })
    },
  })

  const selectedType = listTypes.data?.list_types.find((t) => t.list_type_id === listTypeId)
  const rows = exports.data?.exports ?? []
  /*
   * Role AND entitlement. Kept as separate values rather than one `canCut`
   * because the two need different sentences on screen: "your role cannot cut
   * lists" is an admin conversation and "cutting closed on 3 November" is a
   * renewal, and a single greyed button that says neither generates a support
   * call either way.
   */
  const roleCanCut = campaign?.can_build_universes === true || campaign?.can_cut_walk_lists === true
  const entitledToCut = campaign?.may_cut !== false
  const canCut = roleCanCut && entitledToCut
  // The CSV is Manager-and-above. Printing adds the Field lieutenant, for walk
  // lists only — sheets are what they hand to canvassers.
  const canTake = campaign?.can_view_voters === true && entitledToCut
  const canPrint = (code: string) =>
    entitledToCut &&
    (campaign?.can_view_voters === true || (campaign?.can_cut_walk_lists === true && code === 'walk'))

  return (
    <div>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Lists</h1>
          <p className="mt-1 text-sm text-ink-muted">
            A universe, cut to a list type. Each cut is a fixed artifact — the rows do not change
            underneath it.
          </p>
        </div>
      </div>

      {roleCanCut && !entitledToCut ? (
        /*
         * Not an error — the campaign is working exactly as bought. Bordered and
         * announced rather than coloured: CLAUDE.md keeps red out of the palette
         * entirely, and gold is reserved for warnings.
         */
        <section
          role="status"
          className="mt-6 rounded-lg border border-line bg-surface-sunken p-4 text-sm"
        >
          <h2 className="font-semibold">List cutting has closed for this campaign</h2>
          <p className="mt-1 text-ink-muted">
            {campaign?.cut_until
              ? `Cutting, exporting and printing closed on ${campaign.cut_until}.`
              : 'This campaign has no active entitlement covering list cutting.'}{' '}
            {campaign?.may_report
              ? `Reports and contact history stay available${
                  campaign.report_until ? ` until ${campaign.report_until}` : ''
                }, and lists already cut can still be keyed.`
              : 'Contact history is retained and becomes available again when access is renewed.'}
          </p>
        </section>
      ) : null}

      {canCut ? (
        <section className="mt-6 rounded-lg border border-line bg-surface-sunken p-4">
          <h2 className="text-sm font-semibold">Cut a list</h2>
          <div className="mt-3 flex flex-wrap items-end gap-3">
            <label className="text-sm">
              <span className="mb-1 block text-xs font-medium tracking-wide text-ink-muted uppercase">
                Universe
              </span>
              <select
                className="rounded-md border border-line bg-surface px-2 py-1.5 text-sm text-ink"
                value={universeId}
                onChange={(e) => setUniverseId(e.target.value)}
              >
                {(universes.data?.universes ?? []).length === 0 ? (
                  <option value="">No saved universes</option>
                ) : null}
                {(universes.data?.universes ?? []).map((u) => (
                  <option key={u.universe_id} value={u.universe_id}>
                    {u.name} (v{u.version_no ?? 1})
                  </option>
                ))}
              </select>
            </label>

            <label className="text-sm">
              <span className="mb-1 block text-xs font-medium tracking-wide text-ink-muted uppercase">
                List type
              </span>
              <select
                className="rounded-md border border-line bg-surface px-2 py-1.5 text-sm text-ink"
                value={listTypeId}
                onChange={(e) => setListTypeId(e.target.value)}
              >
                {cuttableTypes.map((t) => (
                  <option key={t.list_type_id} value={t.list_type_id}>
                    {t.name}
                    {t.is_builtin ? '' : ' (custom)'}
                  </option>
                ))}
              </select>
            </label>

            <label className="text-sm">
              <span className="mb-1 block text-xs font-medium tracking-wide text-ink-muted uppercase">
                Name <span className="normal-case">(optional)</span>
              </span>
              <input
                className="w-56 rounded-md border border-line bg-surface px-2 py-1.5 text-sm text-ink"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Saturday canvass, turf 3"
                maxLength={200}
              />
            </label>

            <button
              type="button"
              disabled={!universeId || !listTypeId || cut.isPending}
              onClick={() => cut.mutate()}
              className="rounded-md bg-brand-900 px-3 py-1.5 text-sm font-semibold text-white hover:bg-brand-800 disabled:opacity-50"
            >
              {cut.isPending ? 'Cutting…' : 'Cut list'}
            </button>
          </div>

          {selectedType?.description ? (
            <p className="mt-3 max-w-2xl text-xs text-ink-muted">
              {selectedType.description}
              {selectedType.household_context ? (
                // Worth saying plainly on the screen: the export will contain
                // more people than the universe count the user just read.
                <>
                  {' '}
                  <strong className="font-semibold text-ink">
                    This list also includes everyone else at a matched voter&rsquo;s address
                  </strong>
                  , marked so a canvasser can tell them apart.
                </>
              ) : null}
            </p>
          ) : null}

          {cut.error ? (
            // Same neutral alert the auth screens use. There is no red in the
            // palette on purpose (CLAUDE.md: the UI must never read as
            // party-coded), so an error is marked by role and border, not hue.
            <p
              role="alert"
              className="mt-3 rounded-md border border-line bg-surface px-3 py-2 text-sm text-ink"
            >
              {cut.error instanceof ApiError || cut.error instanceof Error
                ? cut.error.message
                : 'Could not cut that list.'}
            </p>
          ) : null}
        </section>
      ) : (
        <p className="mt-6 text-sm text-ink-muted">
          Cutting a list needs a Campaign Manager role.
        </p>
      )}

      <div className="mt-6 overflow-x-auto">
        <table className="w-full min-w-[52rem] border-collapse text-sm">
          <thead>
            <tr className="border-b border-line text-left text-xs tracking-wide text-ink-muted uppercase">
              <th scope="col" className="py-2 pr-4 font-semibold">List</th>
              <th scope="col" className="py-2 pr-4 font-semibold">Type</th>
              <th scope="col" className="py-2 pr-4 font-semibold">Universe</th>
              <th scope="col" className="py-2 pr-4 font-semibold">Rows</th>
              <th scope="col" className="py-2 pr-4 font-semibold">Status</th>
              <th scope="col" className="py-2 pr-4 font-semibold">Rows expire</th>
              <th scope="col" className="relative py-2 font-semibold"><span className="sr-only">Actions</span></th>
            </tr>
          </thead>
          <tbody>
            {exports.isPending ? (
              <tr><td colSpan={7} className="py-4 text-ink-muted">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={7} className="py-6 text-ink-muted">No lists cut on this campaign yet.</td></tr>
            ) : (
              rows.map((e) => {
                const type = listTypes.data?.list_types.find((t) => t.code === e.list_type_code)
                return (
                  <tr key={e.export_id} className="border-b border-line/60">
                    <td className="py-2 pr-4 font-medium">
                      {e.name ?? <span className="text-ink-muted">Untitled</span>}
                      <div className="text-xs text-ink-muted">{e.requested_at.slice(0, 10)}</div>
                    </td>
                    <td className="py-2 pr-4">{e.list_type_name}</td>
                    <td className="py-2 pr-4 text-ink-muted">
                      {e.universe_name} <span className="tabular-nums">v{e.version_no}</span>
                    </td>
                    <td className="py-2 pr-4 font-mono tabular-nums">
                      {e.row_count === null ? '—' : Number(e.row_count).toLocaleString()}
                    </td>
                    <td className="py-2 pr-4">
                      <Status status={e.status} error={e.error} />
                    </td>
                    <td className="py-2 pr-4 text-ink-muted">
                      {e.status === 'purged'
                        ? 'purged'
                        : (e.rows_expire_at?.slice(0, 10) ?? '—')}
                    </td>
                    <td className="py-2 text-right whitespace-nowrap">
                      {e.status === 'complete' && canPrint(e.list_type_code) ? (
                        <>
                          {isPrintable(type) ? (
                            <>
                              <a
                                // A new tab, not a download: the point is to
                                // look at it and press print.
                                href={`/api/exports/${e.export_id}/sheets`}
                                target="_blank"
                                rel="noreferrer"
                                className="mr-3 text-xs font-semibold text-brand-700 hover:underline"
                              >
                                Print sheets
                              </a>
                              {/* Printed once per walk and kept beside the
                                  scanner, so it is its own link rather than a
                                  page buried inside a 57-page walk. */}
                              <a
                                href={`/api/exports/${e.export_id}/code-sheet`}
                                target="_blank"
                                rel="noreferrer"
                                className="mr-3 text-xs font-semibold text-brand-700 hover:underline"
                              >
                                Code sheet
                              </a>
                            </>
                          ) : null}
                          {canTake ? (
                            <a
                              href={`/api/exports/${e.export_id}/download`}
                              className="text-xs font-semibold text-brand-700 hover:underline"
                            >
                              CSV
                            </a>
                          ) : null}
                        </>
                      ) : null}
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>

      {rows.length > 0 && !canTake ? (
        <p className="mt-3 text-sm text-ink-muted">
          {campaign?.can_cut_walk_lists
            ? 'You can print walk sheets. Other lists and CSV downloads need a Campaign Manager or Consultant.'
            : 'Taking delivery of a list needs a Campaign Manager or Consultant.'}
        </p>
      ) : null}
    </div>
  )
}

function Status({ status, error }: { status: string; error: string | null }) {
  if (status === 'failed') {
    return (
      <span className="font-semibold text-ink underline decoration-dotted" title={error ?? undefined}>
        failed
      </span>
    )
  }
  if (ACTIVE.has(status)) {
    return <span className="text-ink-muted">{status}…</span>
  }
  return <span>{status}</span>
}

/**
 * The current version id of a universe.
 *
 * An export pins a VERSION, and the list of universes carries only the version
 * NUMBER, so cutting needs one more round trip. Fetched at click time rather
 * than held in the table: a version fetched when the page loaded could be stale
 * by the time someone presses the button, and cutting yesterday's definition
 * while the screen says today's is exactly the confusion versions exist to
 * prevent.
 */
async function versionIdFor(universeId: string): Promise<string> {
  const universe = await apiGet<{ universe_version_id: string }>(`/universes/${universeId}`)
  return universe.universe_version_id
}
